<template>
  <div class="event-categories">
    <h1 :style="{ textAlign: 'center' }" v-translate>Describe your emergency</h1>
    <InfoBox class="info-box" :content="content" />
    <div v-for="(eventCategory, i) in selectableEventCategories" :key="i" class="card-container">
      <EventCategoryComponent
        :eventCategory="eventCategory"
        :selected="selectedEventCategoryIds.includes(eventCategory.id)"
        @selected="addEventCategory"
        @unselected="removeEventCategory"
      />
    </div>
    <p
      v-if="otherCategory"
      class="other-category-option"
      @click="onOtherCategorySelected"
      v-translate
    >
      None of these categories fit my emergency.
    </p>
    <span class="covid-error" v-if="displayCovidError" v-translate>
      Please select more than 1 event for COVID
    </span>
    <md-button
      @click="onContinue"
      class="button"
      :class="{ 'disabled-button': disabled }"
      :disabled="disabled"
    >
      <span v-if="!loading" key="next" v-translate>Next: Add Details</span>
      <span v-else key="spinner">
        <LoadSpinner color="white" />
      </span>
    </md-button>
  </div>
</template>

<script>
import EventCategoryComponent from '@/components/requests/EventCategoryComponent'
import InfoBox from '@/components/InfoBox'
import Request from '@/services/requests'
import EventCategory from '@/services/eventCategories'
import LoadSpinner from '@/components/LoadSpinner'
import { mapState } from 'vuex'
import { analyticsMixin } from '@/services/googleAnalytics'

export default {
  name: 'EventCategories',
  mixins: [analyticsMixin],
  components: {
    EventCategoryComponent,
    LoadSpinner,
    InfoBox,
  },
  data() {
    return {
      eventCategories: [],
      selectedEventCategoryIds: [],
      loading: false,
      globalLoading: false,
      displayCovidError: false,
      content: this.$gettext(
        'Please select the type of emergency or emergencies that made it difficult for you to afford basic living expenses.',
      ),
    }
  },
  async created() {
    this.globalLoading = true
    // Pull all Event Categories to populate this page
    const response = await EventCategory.api.list()
    this.eventCategories = response.results
    // Auto-select all Event Categories that have been previously selected for this request
    this.selectedEventCategoryIds = this.currentRequest.eventCategories
    // this.startEditingPing()
    this.globalLoading = false
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    async onContinue() {
      this.loading = true
      this.trackClickedCTA()
      /**
       * checking for id of COVID and if the number of events selected is equal to 1
       * if this is true an error will be thrown
       */
      if (
        this.selectedEventCategoryIds.includes(this.getCOVIDId()) &&
        this.selectedEventCategoryIds.length === 1
      ) {
        // display error for CORONAVIRUS
        this.loading = false
        this.displayCovidError = true
        this.eventsTracker('covid-error', 'Please select more than 1 event for COVID')
      } else {
        // Build an updated Request object, push the update to the API, update state, move on
        const request = Request.create({
          ...this.currentRequest,
          eventCategories: this.selectedEventCategoryIds,
        })
        const updateFields = ['eventCategories']
        const response = await Request.api.update(request, updateFields)
        this.$store.dispatch('updateCurrentRequest', response)
        this.$router.push({ name: 'CreateEvents' })
        this.loading = false
      }
    },
    addEventCategory(val) {
      this.eventsTracker('selected-event-category', `Category ID: ${val}`)
      // User not allowed to select both "Other" and a different category - so first remove Other
      this.removeOtherFromSelected()
      this.selectedEventCategoryIds.push(val)
    },
    async removeEventCategory(val) {
      this.eventsTracker('deselected-event-category', `Category ID: ${val}`)
      this.selectedEventCategoryIds = this.selectedEventCategoryIds.filter((id) => id != val)

      this.currentRequest.eventsRef.forEach(async (oldEvent) => {
        if (oldEvent.categoryRef.id == val) {
          await this.$store.dispatch('partialUpdateEvent', { id: oldEvent.id, removeEvent: true })
        }
      })
    },
    // reducer function to get the id of COVID-19
    getCOVIDId() {
      const value = this.eventCategories.reduce((returnedValue, event) => {
        if (event.name === EventCategory.options.COVID19) {
          returnedValue = event.id
          return returnedValue
        } else {
          return returnedValue
        }
      }, '')
      return value
    },
    onOtherCategorySelected() {
      this.eventsTracker('selected-event-category', `Category ID: ${this.otherCategory.id}`)
      this.selectedEventCategoryIds = [this.otherCategory.id]
      this.onContinue()
    },
    removeOtherFromSelected() {
      if (this.otherCategory && this.selectedEventCategoryIds.includes(this.otherCategory.id)) {
        this.eventsTracker('deselected-event-category', `Category ID: ${this.otherCategory.id}`)
        this.selectedEventCategoryIds = this.selectedEventCategoryIds.filter(
          (id) => id !== this.otherCategory.id,
        )
      }
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function () {
        Request.api.editingPing(request)
      }, 10000)
    },
  },
  computed: {
    ...mapState(['currentRequest']),
    disabled() {
      // Disable button if user hasn't selected any categories, or if page is loading
      if (this.selectedEventCategoryIds.length == 0 || this.loading) {
        return true
      }
      return false
    },
    /* Return all selectable event categories - i.e. all except the public category "Other". */
    selectableEventCategories() {
      return this.eventCategories.filter(
        (eC) => eC.name.toLowerCase() !== 'other' && eC.name.toLowerCase() !== 'otra',
      )
    },
    /* Return public event category "Other", if it is available for this organization; otherwise return null. */
    otherCategory() {
      const otherCategory = this.eventCategories.find(
        (eC) => eC.name.toLowerCase() === 'other' || eC.name.toLowerCase() === 'otra',
      )
      return otherCategory ? otherCategory : null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
.event-categories {
  @include screen-container();
  padding: 2rem 2rem 3rem 2rem;
}
.info-box {
  margin: 1rem;
}
.button {
  @include primary-button(var(--dark-brand-color));
}
.disabled-button {
  @include primary-button-disabled();
}
.card-container {
  width: 100%;
}
.covid-error {
  color: $ruby;
}
.other-category-option {
  color: $blue;
  font-weight: bold;
  width: 96%;
  margin: 1rem 0 2rem 0;

  &:hover {
    cursor: pointer;
  }
}
</style>
